import React, { useMemo, useState, useEffect, useCallback, memo } from 'react';
import { 
  Box, 
  Typography, 
  Chip, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  useTheme,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import { useData } from '../DataProvider';
import { getEventFilterPredicate, createFilterInsignificantActionsPredicate } from '@appesteem/aeshared-js/loadwatch';
import { getBucketLabel, getDownloadInfoLabel } from '../bucketLabels';

// Component for expandable table row with metadata
const ExpandableTableRow = ({ row, spansToUse, selectedSpanName, theme, renderLifecycleChips, expandedEventsRows, toggleEventsRow, allRows, setSeekTo, formatTime, cachedFilterPredicate, platform }) => {
  const [expanded, setExpanded] = React.useState(false);
  // For ProcessLifeCycles, only show dropdown if there are children
  const hasMetadata = row.sectionName === 'ProcessLifeCycles'
    ? (row.metadata?.Children && Array.isArray(row.metadata.Children) && row.metadata.Children.length > 0)
    : (row.metadata !== null && Object.keys(row.metadata).length > 0);
  
  // Generate unique key for this row
  const rowKey = `${row.sectionName}-${row.itemName}-${row.processName || ''}`;
  const eventsExpanded = expandedEventsRows.has(rowKey);
  
  // Lazy loading state for filtered events
  const [filteredEvents, setFilteredEvents] = React.useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = React.useState(false);
  const [eventsLoaded, setEventsLoaded] = React.useState(false);
  
  // Lazily compute filtered events only when events row is expanded
  React.useEffect(() => {
    if (!eventsExpanded || eventsLoaded) return;
    if (!allRows || !Array.isArray(allRows)) {
      setFilteredEvents([]);
      setEventsLoaded(true);
      return;
    }
    
    setIsLoadingEvents(true);
    // Use requestAnimationFrame to allow UI to update first
    const rafId = requestAnimationFrame(() => {
      // Use setTimeout to defer heavy computation
      setTimeout(() => {
        try {
          const predicate = getEventFilterPredicate(row.itemName, row.sectionName, allRows, cachedFilterPredicate, platform);
          if (predicate) {
            const results = allRows.filter(predicate);
            setFilteredEvents(results);
          } else {
            setFilteredEvents([]);
          }
        } catch (e) {
          console.error('Error filtering events:', e);
          setFilteredEvents([]);
        }
        setIsLoadingEvents(false);
        setEventsLoaded(true);
      }, 0);
    });
    
    return () => cancelAnimationFrame(rafId);
  }, [eventsExpanded, eventsLoaded, allRows, row.itemName, row.sectionName, cachedFilterPredicate, platform]);
  
  // Reset loaded state if row changes
  React.useEffect(() => {
    setEventsLoaded(false);
    setFilteredEvents([]);
  }, [row.itemName, row.sectionName]);

  return (
    <React.Fragment>
      <TableRow
        hover
        sx={{
          cursor: hasMetadata ? 'pointer' : 'default',
          '&:hover': hasMetadata ? { backgroundColor: 'action.hover' } : {},
        }}
        onClick={() => hasMetadata && setExpanded(!expanded)}
      >
        <TableCell sx={{ maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Always reserve space for icon button to prevent shifting */}
            <Box sx={{ width: row.sectionName === 'ProcessLifeCycles' ? '24px' : (hasMetadata ? '24px' : '0px'), flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {hasMetadata && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(!expanded);
                  }}
                  sx={{ padding: '2px', flexShrink: 0 }}
                >
                  {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                </IconButton>
              )}
            </Box>
            {/* Always reserve space for children icon in ProcessLifeCycles to keep item names aligned */}
            <Box sx={{ width: row.sectionName === 'ProcessLifeCycles' ? '20px' : '0px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {row.sectionName === 'ProcessLifeCycles' && row.metadata?.Children && Array.isArray(row.metadata.Children) && row.metadata.Children.length > 0 && (
                <AccountTreeIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
              )}
            </Box>
            <Typography 
              component="div"
              variant="body2" 
              sx={{ 
                fontSize: '0.75rem', 
                whiteSpace: 'normal', 
                wordBreak: 'break-word', 
                flex: 1,
                cursor: 'pointer',
                color: eventsExpanded ? 'primary.main' : 'inherit',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.main'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleEventsRow(rowKey);
              }}
              title={eventsExpanded ? 'Hide events' : 'Show events'}
            >
              {row.itemName}
              {/* Show Details inline for ProcessLifeCycles */}
              {row.sectionName === 'ProcessLifeCycles' && row.metadata?.Details && (
                <Typography component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 0.5, fontFamily: 'monospace', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  - {row.metadata.Details}
                </Typography>
              )}
              {row.metadata?.FileInfo?.FileType && (
                <Chip 
                  label={row.metadata.FileInfo.FileType} 
                  size="small" 
                  sx={{ ml: 0.5, height: 16, fontSize: '0.65rem' }}
                  variant="outlined"
                />
              )}
              {/* Add VirusTotal link for InterestingFileInfo and InstallerInfo sections */}
              {(row.sectionName === 'InterestingFileInfo' || row.sectionName === 'InstallerInfo') && row.metadata?.FileInfo?.SHA256 && (
                <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                  <Typography
                    component="a"
                    href={`https://www.virustotal.com/#/file/${row.metadata.FileInfo.SHA256}/detection`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontSize: '0.7rem',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    VirusTotal
                  </Typography>
                </Box>
              )}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.processName}</TableCell>
        {spansToUse.map((span) => {
          const spanName = span.scenario || 'unnamed';
          const isSelected = spanName === selectedSpanName;
          const cellValue = row.spanData[spanName];
          
          return (
            <TableCell
              key={spanName}
              sx={{
                maxWidth: 100,
                textAlign: 'center',
                bgcolor: isSelected ? 'action.selected' : 'inherit',
                borderLeft: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                borderRight: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
              }}
            >
              {row.dataType === 'boolean' ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {cellValue ? (
                    <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  ) : (
                    <Typography sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>—</Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {renderLifecycleChips(cellValue || '')}
                </Box>
              )}
            </TableCell>
          );
        })}
      </TableRow>
      {hasMetadata && (
        <TableRow>
          <TableCell colSpan={3 + spansToUse.length} sx={{ py: 1, bgcolor: 'background.default' }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 4 }}>
                {row.metadata.FileInfo && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>File Info:</Typography>
                    {Object.entries(row.metadata.FileInfo).map(([key, value]) => (
                      <Typography key={key} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </Typography>
                    ))}
                  </Box>
                )}
                {row.metadata.CertInfo && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Certificate Info:</Typography>
                    {Object.entries(row.metadata.CertInfo).map(([key, value]) => (
                      <Typography key={key} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </Typography>
                    ))}
                  </Box>
                )}
                {row.metadata.BitsInfo && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>{getDownloadInfoLabel(platform)}</Typography>
                    {Object.entries(row.metadata.BitsInfo).map(([key, value]) => {
                      // Format dates nicely
                      let displayValue = value;
                      if (value instanceof Date) {
                        displayValue = value.toISOString();
                      } else if (key === 'StartTime' || key === 'EndTime' || key === 'FileTime') {
                        try {
                          const date = new Date(value);
                          if (!isNaN(date.getTime())) {
                            displayValue = date.toISOString();
                          }
                        } catch (e) {
                          // Keep original value if parsing fails
                        }
                      }
                      return (
                        <Typography key={key} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          {key}: {typeof displayValue === 'object' && displayValue !== null ? JSON.stringify(displayValue) : String(displayValue || '')}
                        </Typography>
                      );
                    })}
                  </Box>
                )}
                {row.metadata.ProcInfo && row.sectionName !== 'ProcessLifeCycles' && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Process Info:</Typography>
                    {row.metadata.ProcInfo.ProcessName && (
                      <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        ProcessName: {row.metadata.ProcInfo.ProcessName}
                      </Typography>
                    )}
                    {row.metadata.ProcInfo.ParentUProcessID !== undefined && (
                      <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        Parent: {row.metadata.ProcInfo.ParentUProcessID}
                      </Typography>
                    )}
                    {row.metadata.ProcInfo.Actions && Array.isArray(row.metadata.ProcInfo.Actions) && (
                      <Box>
                        {row.metadata.ProcInfo.Actions.map((action, actionIdx) => {
                          if (action.Action?.includes('ProcessStart')) {
                            return (
                              <Typography key={actionIdx} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                Details: {action.Details || ''}
                              </Typography>
                            );
                          }
                          return null;
                        })}
                      </Box>
                    )}
                  </Box>
                )}
                {/* Display children for ProcessLifeCycles in table format aligned with parent */}
                {row.metadata.Children && Array.isArray(row.metadata.Children) && row.metadata.Children.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Children:</Typography>
                    <Box sx={{ display: 'table', width: '100%', borderCollapse: 'collapse' }}>
                      {row.metadata.Children.map((child, childIdx) => {
                        const childLifecycleData = {};
                        spansToUse.forEach((span) => {
                          const spanName = span.scenario || 'unnamed';
                          childLifecycleData[spanName] = child.lifecycleMap && typeof child.lifecycleMap === 'object' 
                            ? (child.lifecycleMap[spanName] || '') 
                            : '';
                        });
                        
                        return (
                          <Box 
                            key={childIdx} 
                            sx={{ 
                              display: 'table-row',
                              bgcolor: childIdx % 2 === 0 ? 'background.default' : 'transparent',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            {/* Section column - empty */}
                            <Box sx={{ display: 'table-cell', width: 120, maxWidth: 120, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                └─
                              </Typography>
                            </Box>
                            
                            {/* Item Name column */}
                            <Box sx={{ display: 'table-cell', width: '30%', maxWidth: 300, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                {child.name}
                                {child.details && (
                                  <Typography component="span" sx={{ fontSize: '0.65rem', color: 'text.secondary', ml: 0.5, fontFamily: 'monospace', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    - {child.details}
                                  </Typography>
                                )}
                              </Typography>
                            </Box>
                            
                            {/* Process Name column - empty */}
                            <Box sx={{ display: 'table-cell', width: '20%', maxWidth: 200, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', fontFamily: 'monospace' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                Child
                              </Typography>
                            </Box>
                            
                            {/* Scenario columns */}
                            {spansToUse.map((span) => {
                              const spanName = span.scenario || 'unnamed';
                              const isSelected = spanName === selectedSpanName;
                              const childLifecycle = childLifecycleData[spanName] || '';
                              const spanColumnWidth = spansToUse.length > 0 ? `${50 / spansToUse.length}%` : '100px';
                              
                              return (
                                <Box
                                  key={spanName}
                                  sx={{
                                    display: 'table-cell',
                                    width: spanColumnWidth,
                                    maxWidth: 100,
                                    px: 1,
                                    py: 0.5,
                                    textAlign: 'center',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: isSelected ? 'action.selected' : 'inherit',
                                    borderLeft: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                                    borderRight: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', justifyContent: 'center' }}>
                                    {renderLifecycleChips(childLifecycle)}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                {Object.entries(row.metadata)
                  .filter(([key]) => !['FileInfo', 'CertInfo', 'BitsInfo', 'ProcInfo', 'Children', 'Details'].includes(key))
                  .map(([key, value]) => (
                    <Typography key={key} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </Typography>
                  ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
      {/* Events table row */}
      <TableRow>
        <TableCell colSpan={3 + spansToUse.length} sx={{ py: 0, bgcolor: 'background.paper' }}>
          <Collapse in={eventsExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                Events {isLoadingEvents ? '' : `(${filteredEvents.length})`}
              </Typography>
              {isLoadingEvents ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Loading events...</Typography>
                </Box>
              ) : filteredEvents.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.7rem', padding: '4px 8px' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: '12%' }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '15%' }}>Process</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '15%' }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '12%' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '25%' }}>Item Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '21%' }}>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEvents.map((event, idx) => (
                        <TableRow
                          key={idx}
                          hover
                          sx={{
                            cursor: typeof event.VideoOffset === 'number' ? 'pointer' : 'default',
                            '&:hover': typeof event.VideoOffset === 'number' ? { backgroundColor: 'action.hover' } : {},
                          }}
                          onClick={() => {
                            if (typeof event.VideoOffset === 'number' && !isNaN(event.VideoOffset) && setSeekTo) {
                              setSeekTo(event.VideoOffset / 1000);
                            }
                          }}
                        >
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {typeof event.VideoOffset === 'number' && !isNaN(event.VideoOffset)
                              ? formatTime(event.VideoOffset)
                              : '-'}
                            {typeof event.VideoOffset === 'number' && !isNaN(event.VideoOffset) && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (setSeekTo) {
                                    setSeekTo(event.VideoOffset / 1000);
                                  }
                                }}
                                sx={{ padding: '2px', ml: 0.5, verticalAlign: 'middle' }}
                                title="Jump to in video"
                              >
                                <SmartDisplayOutlinedIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {event.UPid !== undefined && event.UPid !== null
                              ? `${event.UPid}/${event.ProcessName || 'unknown'}`
                              : (event.ProcessName || '-')}
                          </TableCell>
                          <TableCell>{event.Action || '-'}</TableCell>
                          <TableCell>{event.EventType || '-'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {event.ItemName || '-'}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {(() => {
                              const details = [];
                              if (event.Details) {
                                details.push(event.Details);
                              }
                              // Add BytesOut for File events
                              if (event.EventType === 'File' && event.Action === 'FileIOWrite') {
                                const bytesOut = typeof event.BytesOut === 'number' ? event.BytesOut : null;
                                if (bytesOut !== null) {
                                  details.push(`BytesOut: ${bytesOut.toLocaleString()}`);
                                }
                              }
                              // Add BytesIn and BytesOut for TcpIp events
                              if (event.EventType === 'TcpIp') {
                                const bytesIn = typeof event.BytesIn === 'number' ? event.BytesIn : null;
                                const bytesOut = typeof event.BytesOut === 'number' ? event.BytesOut : null;
                                if (bytesIn !== null || bytesOut !== null) {
                                  const bytesParts = [];
                                  if (bytesIn !== null) {
                                    bytesParts.push(`BytesIn: ${bytesIn.toLocaleString()}`);
                                  }
                                  if (bytesOut !== null) {
                                    bytesParts.push(`BytesOut: ${bytesOut.toLocaleString()}`);
                                  }
                                  if (bytesParts.length > 0) {
                                    details.push(bytesParts.join(', '));
                                  }
                                }
                              }
                              return details.length > 0 ? details.join(' | ') : '-';
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="caption" color="text.secondary">No events found</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

// Component for grouped table row with multiple process lines
const GroupedTableRow = ({ row, spansToUse, selectedSpanName, theme, renderLifecycleChips, expandedEventsRows, toggleEventsRow, allRows, setSeekTo, formatTime, cachedFilterPredicate, platform }) => {
  const [expanded, setExpanded] = React.useState(false);
  // For ProcessLifeCycles, only show dropdown if there are children
  const hasMetadata = row.sectionName === 'ProcessLifeCycles'
    ? (row.metadata?.Children && Array.isArray(row.metadata.Children) && row.metadata.Children.length > 0)
    : (row.metadata !== null && Object.keys(row.metadata).length > 0);
  const processLines = row.processLines || [];
  const rowSpan = processLines.length;
  
  // Generate unique key for this row (use first process line for key)
  const rowKey = `${row.sectionName}-${row.itemName}-${processLines.length > 0 ? processLines[0].processName : ''}`;
  const eventsExpanded = expandedEventsRows.has(rowKey);
  
  // Lazy loading state for filtered events
  const [filteredEvents, setFilteredEvents] = React.useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = React.useState(false);
  const [eventsLoaded, setEventsLoaded] = React.useState(false);
  
  // Lazily compute filtered events only when events row is expanded
  React.useEffect(() => {
    if (!eventsExpanded || eventsLoaded) return;
    if (!allRows || !Array.isArray(allRows)) {
      setFilteredEvents([]);
      setEventsLoaded(true);
      return;
    }
    
    setIsLoadingEvents(true);
    // Use requestAnimationFrame to allow UI to update first
    const rafId = requestAnimationFrame(() => {
      // Use setTimeout to defer heavy computation
      setTimeout(() => {
        try {
          const predicate = getEventFilterPredicate(row.itemName, row.sectionName, allRows, cachedFilterPredicate, platform);
          if (predicate) {
            const results = allRows.filter(predicate);
            setFilteredEvents(results);
          } else {
            setFilteredEvents([]);
          }
        } catch (e) {
          console.error('Error filtering events:', e);
          setFilteredEvents([]);
        }
        setIsLoadingEvents(false);
        setEventsLoaded(true);
      }, 0);
    });
    
    return () => cancelAnimationFrame(rafId);
  }, [eventsExpanded, eventsLoaded, allRows, row.itemName, row.sectionName, cachedFilterPredicate, platform]);
  
  // Reset loaded state if row changes
  React.useEffect(() => {
    setEventsLoaded(false);
    setFilteredEvents([]);
  }, [row.itemName, row.sectionName]);

  return (
    <React.Fragment>
      {processLines.map((processLine, idx) => (
        <TableRow
          key={idx}
          hover
          sx={{
            cursor: hasMetadata && idx === 0 ? 'pointer' : 'default',
            '&:hover': hasMetadata && idx === 0 ? { backgroundColor: 'action.hover' } : {},
          }}
          onClick={() => hasMetadata && idx === 0 && setExpanded(!expanded)}
        >
          {/* Item Name column - only show on first row, with rowSpan */}
          {idx === 0 && (
            <TableCell
              rowSpan={rowSpan}
              sx={{
                maxWidth: 300,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                verticalAlign: 'top',
                borderBottom: rowSpan > 1 ? 'none' : undefined,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* Always reserve space for icon button to prevent shifting */}
                <Box sx={{ width: row.sectionName === 'ProcessLifeCycles' ? '24px' : (hasMetadata ? '24px' : '0px'), flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                  {hasMetadata && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(!expanded);
                      }}
                      sx={{ padding: '2px', flexShrink: 0 }}
                    >
                      {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </IconButton>
                  )}
                </Box>
                {/* Always reserve space for children icon in ProcessLifeCycles to keep item names aligned */}
                <Box sx={{ width: row.sectionName === 'ProcessLifeCycles' ? '20px' : '0px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                  {row.sectionName === 'ProcessLifeCycles' && row.metadata?.Children && Array.isArray(row.metadata.Children) && row.metadata.Children.length > 0 && (
                    <AccountTreeIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
                  )}
                </Box>
                <Typography 
                  component="div"
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.75rem', 
                    whiteSpace: 'normal', 
                    wordBreak: 'break-word', 
                    flex: 1,
                    cursor: 'pointer',
                    color: eventsExpanded ? 'primary.main' : 'inherit',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: 'primary.main'
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleEventsRow(rowKey);
                  }}
                  title={eventsExpanded ? 'Hide events' : 'Show events'}
                >
                  {row.itemName}
                  {/* Show Details inline for ProcessLifeCycles */}
                  {row.sectionName === 'ProcessLifeCycles' && row.metadata?.Details && (
                    <Typography component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 0.5, fontFamily: 'monospace', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      - {row.metadata.Details}
                    </Typography>
                  )}
                  {row.metadata?.FileInfo?.FileType && (
                    <Chip 
                      label={row.metadata.FileInfo.FileType} 
                      size="small" 
                      sx={{ ml: 0.5, height: 16, fontSize: '0.65rem' }}
                      variant="outlined"
                    />
                  )}
                  {/* Add VirusTotal link for InterestingFileInfo and InstallerInfo sections */}
                  {(row.sectionName === 'InterestingFileInfo' || row.sectionName === 'InstallerInfo') && row.metadata?.FileInfo?.SHA256 && (
                    <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                      <Typography
                        component="a"
                        href={`https://www.virustotal.com/#/file/${row.metadata.FileInfo.SHA256}/detection`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontSize: '0.7rem',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        VirusTotal
                      </Typography>
                    </Box>
                  )}
                </Typography>
              </Box>
            </TableCell>
          )}
          
          {/* Process Name column */}
          <TableCell sx={{ fontFamily: 'monospace', maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {processLine.processName}
          </TableCell>
          
          {/* Span columns */}
          {spansToUse.map((span) => {
            const spanName = span.scenario || 'unnamed';
            const isSelected = spanName === selectedSpanName;
            const cellValue = processLine.spanData[spanName];
            
            return (
              <TableCell
                key={spanName}
                sx={{
                  maxWidth: 100,
                  textAlign: 'center',
                  bgcolor: isSelected ? 'action.selected' : 'inherit',
                  borderLeft: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                  borderRight: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                }}
              >
                {processLine.dataType === 'boolean' ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {cellValue ? (
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    ) : (
                      <Typography sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>—</Typography>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {renderLifecycleChips(cellValue || '')}
                  </Box>
                )}
              </TableCell>
            );
          })}
        </TableRow>
      ))}
      
      {/* Metadata expansion row */}
      {hasMetadata && (
        <TableRow>
          <TableCell colSpan={1 + 1 + spansToUse.length} sx={{ py: 1, bgcolor: 'background.default' }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 4 }}>
                {row.metadata.FileInfo && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>File Info:</Typography>
                    {Object.entries(row.metadata.FileInfo).map(([key, value]) => (
                      <Typography key={key} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </Typography>
                    ))}
                  </Box>
                )}
                {row.metadata.CertInfo && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Certificate Info:</Typography>
                    {Object.entries(row.metadata.CertInfo).map(([key, value]) => (
                      <Typography key={key} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </Typography>
                    ))}
                  </Box>
                )}
                {row.metadata.BitsInfo && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>{getDownloadInfoLabel(platform)}</Typography>
                    {Object.entries(row.metadata.BitsInfo).map(([key, value]) => {
                      // Format dates nicely
                      let displayValue = value;
                      if (value instanceof Date) {
                        displayValue = value.toISOString();
                      } else if (key === 'StartTime' || key === 'EndTime' || key === 'FileTime') {
                        try {
                          const date = new Date(value);
                          if (!isNaN(date.getTime())) {
                            displayValue = date.toISOString();
                          }
                        } catch (e) {
                          // Keep original value if parsing fails
                        }
                      }
                      return (
                        <Typography key={key} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          {key}: {typeof displayValue === 'object' && displayValue !== null ? JSON.stringify(displayValue) : String(displayValue || '')}
                        </Typography>
                      );
                    })}
                  </Box>
                )}
                {row.metadata.ProcInfo && row.sectionName !== 'ProcessLifeCycles' && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Process Info:</Typography>
                    {row.metadata.ProcInfo.ProcessName && (
                      <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        ProcessName: {row.metadata.ProcInfo.ProcessName}
                      </Typography>
                    )}
                    {row.metadata.ProcInfo.ParentUProcessID !== undefined && (
                      <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        Parent: {row.metadata.ProcInfo.ParentUProcessID}
                      </Typography>
                    )}
                    {row.metadata.ProcInfo.Actions && Array.isArray(row.metadata.ProcInfo.Actions) && (
                      <Box>
                        {row.metadata.ProcInfo.Actions.map((action, actionIdx) => {
                          if (action.Action?.includes('ProcessStart')) {
                            return (
                              <Typography key={actionIdx} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                Details: {action.Details || ''}
                              </Typography>
                            );
                          }
                          return null;
                        })}
                      </Box>
                    )}
                  </Box>
                )}
                {/* Display children for ProcessLifeCycles in table format aligned with parent */}
                {row.metadata.Children && Array.isArray(row.metadata.Children) && row.metadata.Children.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Children:</Typography>
                    <Box sx={{ display: 'table', width: '100%', borderCollapse: 'collapse' }}>
                      {row.metadata.Children.map((child, childIdx) => {
                        const childLifecycleData = {};
                        spansToUse.forEach((span) => {
                          const spanName = span.scenario || 'unnamed';
                          childLifecycleData[spanName] = child.lifecycleMap && typeof child.lifecycleMap === 'object' 
                            ? (child.lifecycleMap[spanName] || '') 
                            : '';
                        });
                        
                        return (
                          <Box 
                            key={childIdx} 
                            sx={{ 
                              display: 'table-row',
                              bgcolor: childIdx % 2 === 0 ? 'background.default' : 'transparent',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            {/* Section column - empty */}
                            <Box sx={{ display: 'table-cell', width: 120, maxWidth: 120, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                └─
                              </Typography>
                            </Box>
                            
                            {/* Item Name column */}
                            <Box sx={{ display: 'table-cell', width: '30%', maxWidth: 300, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                {child.name}
                                {child.details && (
                                  <Typography component="span" sx={{ fontSize: '0.65rem', color: 'text.secondary', ml: 0.5, fontFamily: 'monospace', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    - {child.details}
                                  </Typography>
                                )}
                              </Typography>
                            </Box>
                            
                            {/* Process Name column - empty */}
                            <Box sx={{ display: 'table-cell', width: '20%', maxWidth: 200, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', fontFamily: 'monospace' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                Child
                              </Typography>
                            </Box>
                            
                            {/* Scenario columns */}
                            {spansToUse.map((span) => {
                              const spanName = span.scenario || 'unnamed';
                              const isSelected = spanName === selectedSpanName;
                              const childLifecycle = childLifecycleData[spanName] || '';
                              const spanColumnWidth = spansToUse.length > 0 ? `${50 / spansToUse.length}%` : '100px';
                              
                              return (
                                <Box
                                  key={spanName}
                                  sx={{
                                    display: 'table-cell',
                                    width: spanColumnWidth,
                                    maxWidth: 100,
                                    px: 1,
                                    py: 0.5,
                                    textAlign: 'center',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: isSelected ? 'action.selected' : 'inherit',
                                    borderLeft: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                                    borderRight: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', justifyContent: 'center' }}>
                                    {renderLifecycleChips(childLifecycle)}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                {Object.entries(row.metadata)
                  .filter(([key]) => !['FileInfo', 'CertInfo', 'BitsInfo', 'ProcInfo', 'Children', 'Details'].includes(key))
                  .map(([key, value]) => (
                    <Typography key={key} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </Typography>
                  ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
      {/* Events table row */}
      <TableRow>
        <TableCell colSpan={1 + 1 + spansToUse.length} sx={{ py: 0, bgcolor: 'background.paper' }}>
          <Collapse in={eventsExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                Events {isLoadingEvents ? '' : `(${filteredEvents.length})`}
              </Typography>
              {isLoadingEvents ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Loading events...</Typography>
                </Box>
              ) : filteredEvents.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.7rem', padding: '4px 8px' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: '12%' }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '15%' }}>Process</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '15%' }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '12%' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '25%' }}>Item Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '21%' }}>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEvents.map((event, idx) => (
                        <TableRow
                          key={idx}
                          hover
                          sx={{
                            cursor: typeof event.VideoOffset === 'number' ? 'pointer' : 'default',
                            '&:hover': typeof event.VideoOffset === 'number' ? { backgroundColor: 'action.hover' } : {},
                          }}
                          onClick={() => {
                            if (typeof event.VideoOffset === 'number' && !isNaN(event.VideoOffset) && setSeekTo) {
                              setSeekTo(event.VideoOffset / 1000);
                            }
                          }}
                        >
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {typeof event.VideoOffset === 'number' && !isNaN(event.VideoOffset)
                              ? formatTime(event.VideoOffset)
                              : '-'}
                            {typeof event.VideoOffset === 'number' && !isNaN(event.VideoOffset) && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (setSeekTo) {
                                    setSeekTo(event.VideoOffset / 1000);
                                  }
                                }}
                                sx={{ padding: '2px', ml: 0.5, verticalAlign: 'middle' }}
                                title="Jump to in video"
                              >
                                <SmartDisplayOutlinedIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {event.UPid !== undefined && event.UPid !== null
                              ? `${event.UPid}/${event.ProcessName || 'unknown'}`
                              : (event.ProcessName || '-')}
                          </TableCell>
                          <TableCell>{event.Action || '-'}</TableCell>
                          <TableCell>{event.EventType || '-'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {event.ItemName || '-'}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {(() => {
                              const details = [];
                              if (event.Details) {
                                details.push(event.Details);
                              }
                              // Add BytesOut for File events
                              if (event.EventType === 'File' && event.Action === 'FileIOWrite') {
                                const bytesOut = typeof event.BytesOut === 'number' ? event.BytesOut : null;
                                if (bytesOut !== null) {
                                  details.push(`BytesOut: ${bytesOut.toLocaleString()}`);
                                }
                              }
                              // Add BytesIn and BytesOut for TcpIp events
                              if (event.EventType === 'TcpIp') {
                                const bytesIn = typeof event.BytesIn === 'number' ? event.BytesIn : null;
                                const bytesOut = typeof event.BytesOut === 'number' ? event.BytesOut : null;
                                if (bytesIn !== null || bytesOut !== null) {
                                  const bytesParts = [];
                                  if (bytesIn !== null) {
                                    bytesParts.push(`BytesIn: ${bytesIn.toLocaleString()}`);
                                  }
                                  if (bytesOut !== null) {
                                    bytesParts.push(`BytesOut: ${bytesOut.toLocaleString()}`);
                                  }
                                  if (bytesParts.length > 0) {
                                    details.push(bytesParts.join(', '));
                                  }
                                }
                              }
                              return details.length > 0 ? details.join(' | ') : '-';
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="caption" color="text.secondary">No events found</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

// Default number of items to show per section (before "Show more")
const DEFAULT_SECTION_LIMIT = 50;

const EventSummaries = memo(({ appInfo }) => {
  const { selectedScenario, eventSummaries: allSummaries, spans, allRows, setSeekTo, setSummariesEnabled, summariesLoading, summariesProgress, recompute, hugeLogMode, installerFileInfo, metadata, platform } = useData();

  // Enable expensive summary computation only when this panel is mounted (important for huge logs)
  useEffect(() => {
    if (setSummariesEnabled) setSummariesEnabled(true);
    return () => {
      if (setSummariesEnabled && hugeLogMode) setSummariesEnabled(false);
    };
  }, [setSummariesEnabled, hugeLogMode]);

  const startMs = selectedScenario && typeof selectedScenario.startMs === 'number' ? selectedScenario.startMs : null;
  const endMs = selectedScenario && typeof selectedScenario.endMs === 'number' ? selectedScenario.endMs : null;

  const [expandedSections, setExpandedSections] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [recomputeConfirmOpen, setRecomputeConfirmOpen] = useState(false);
  const [expandedEventsRows, setExpandedEventsRows] = useState(new Set());
  const [loadingSections, setLoadingSections] = useState(new Set());
  // Track how many items to show per section (for progressive loading)
  const [sectionLimits, setSectionLimits] = useState({});
  
  // Cache the filter predicate to avoid recomputing for each row
  const cachedFilterPredicate = useMemo(() => {
    if (!allRows || !Array.isArray(allRows) || allRows.length === 0) return null;
    return createFilterInsignificantActionsPredicate(allRows, platform);
  }, [allRows, platform]);
  
  // Get current limit for a section
  const getSectionLimit = useCallback((sectionName) => {
    return sectionLimits[sectionName] || DEFAULT_SECTION_LIMIT;
  }, [sectionLimits]);
  
  // Show more items in a section
  const showMoreInSection = useCallback((sectionName, totalCount) => {
    setSectionLimits(prev => ({
      ...prev,
      [sectionName]: Math.min((prev[sectionName] || DEFAULT_SECTION_LIMIT) + 50, totalCount)
    }));
  }, []);
  
  // Show all items in a section
  const showAllInSection = useCallback((sectionName, totalCount) => {
    setSectionLimits(prev => ({
      ...prev,
      [sectionName]: totalCount
    }));
  }, []);
  
  // Reset to default limit
  const showLessInSection = useCallback((sectionName) => {
    setSectionLimits(prev => ({
      ...prev,
      [sectionName]: DEFAULT_SECTION_LIMIT
    }));
  }, []);

  // Download the currently-displayed event summaries as event_summaries.json. Includes
  // installerInfo (a separate artifact the panel renders as its own section) so the export
  // matches the panel; the stored/runner event_summaries.json stays summaries-only.
  const handleDownloadSummaries = useCallback(() => {
    try {
      const payload = {};
      if (installerFileInfo) payload.installerInfo = installerFileInfo;
      payload.summaries = allSummaries || {};
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'event_summaries.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke so the browser has committed the download before we free the blob URL
      // (revoking on the same tick can cancel the download in some browsers).
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Failed to download event summaries:', err);
    }
  }, [allSummaries, installerFileInfo]);

  // Toggle expanded events row
  const toggleEventsRow = (rowKey) => {
    setExpandedEventsRows(prev => {
      const updated = new Set(prev);
      if (updated.has(rowKey)) {
        updated.delete(rowKey);
      } else {
        updated.add(rowKey);
      }
      return updated;
    });
  };

  // Build spans array: always include "all", plus all spans from DataProvider
  // Sort by startActionMs (earliest first), then endActionMs (earliest first), with "all" last
  const spansToUse = useMemo(() => {
    const allSpans = Array.isArray(spans) ? [...spans] : [];
    const allSpan = { scenario: 'all', startActionMs: null, endActionMs: null };
    
    // Separate "all" span from other spans
    const otherSpans = allSpans.filter(span => (span.scenario || 'unnamed') !== 'all');
    
    // Sort by startActionMs (treating null/undefined as Infinity), then by endActionMs
    const sortedSpans = otherSpans.sort((a, b) => {
      const aStart = a.startActionMs ?? Infinity;
      const bStart = b.startActionMs ?? Infinity;
      
      if (aStart !== bStart) {
        return aStart - bStart;
      }
      
      // If start times are equal, sort by end time
      const aEnd = a.endActionMs ?? Infinity;
      const bEnd = b.endActionMs ?? Infinity;
      return aEnd - bEnd;
    });
    
    // Add "all" span at the end
    return [...sortedSpans, allSpan];
  }, [spans]);

  // Initialize/update expanded sections when allSummaries changes
  React.useEffect(() => {
    const newSections = new Set();
    for (const [sectionName] of Object.entries(allSummaries || {})) {
      newSections.add(sectionName);
    }
    setExpandedSections(prev => {
      // If empty, keep it empty (default to collapsed)
      if (prev.size === 0) {
        return new Set();
      }
      // Otherwise, add any new sections but keep existing state
      const updated = new Set(prev);
      for (const section of newSections) {
        if (!updated.has(section)) {
          updated.add(section);
        }
      }
      return updated;
    });
  }, [allSummaries]);

  const toggleSection = useCallback((sectionName) => {
    setExpandedSections(prev => {
      const updated = new Set(prev);
      if (updated.has(sectionName)) {
        updated.delete(sectionName);
        return updated;
      } else {
        // Show loading state before expanding (for large sections)
        setLoadingSections(loadingPrev => {
          const loadingUpdated = new Set(loadingPrev);
          loadingUpdated.add(sectionName);
          return loadingUpdated;
        });
        
        // Defer the actual expansion to allow UI to show loading indicator
        requestAnimationFrame(() => {
          setTimeout(() => {
            setExpandedSections(innerPrev => {
              const innerUpdated = new Set(innerPrev);
              innerUpdated.add(sectionName);
              return innerUpdated;
            });
            // Remove loading state after a brief delay
            setTimeout(() => {
              setLoadingSections(loadingPrev => {
                const loadingUpdated = new Set(loadingPrev);
                loadingUpdated.delete(sectionName);
                return loadingUpdated;
              });
            }, 50);
          }, 0);
        });
        
        return prev; // Don't change immediately, let the deferred update handle it
      }
    });
  }, []);

  const toggleAllSections = () => {
    const allSectionNames = new Set();
    if (installerFileInfo) allSectionNames.add('InstallerInfo');
    for (const [sectionName] of Object.entries(allSummaries || {})) {
      allSectionNames.add(sectionName);
    }
    
    setExpandedSections(prev => {
      // Check if all sections are expanded
      const allExpanded = allSectionNames.size > 0 && Array.from(allSectionNames).every(name => prev.has(name));
      
      if (allExpanded) {
        // Collapse all
        return new Set();
      } else {
        // Expand all
        return new Set(allSectionNames);
      }
    });
  };

  const theme = useTheme();
  const selectedSpanName = selectedScenario ? selectedScenario.name : 'all';

  // Helper function to render lifecycle chips
  const renderLifecycleChips = (lifeCycleString) => {
    if (!lifeCycleString || typeof lifeCycleString !== 'string') return null;
    return String(lifeCycleString || '').split('').map((ch, i) => {
      const code = ch || '.';
      const isUpper = code === code.toUpperCase();
      if (code === 'B' || code === 'b') {
        if (isUpper) {
          return <Chip key={i} label={code} size="small" sx={(theme) => ({ minWidth: 22, height: 20, backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText, fontWeight: 700, fontSize: '0.7rem', borderRadius: 4 })} />;
        }
        return <Chip key={i} label={code} size="small" variant="outlined" sx={(theme) => ({ minWidth: 22, height: 20, color: theme.palette.primary.main, borderColor: theme.palette.primary.main, fontWeight: 700, fontSize: '0.7rem', borderRadius: 4 })} />;
      }
      if (code === 'E' || code === 'e') {
        if (isUpper) {
          return <Chip key={i} label={code} size="small" sx={(theme) => ({ minWidth: 22, height: 20, backgroundColor: theme.palette.secondary.main, color: theme.palette.secondary.contrastText, fontWeight: 700, fontSize: '0.7rem', borderRadius: 4 })} />;
        }
        return <Chip key={i} label={code} size="small" variant="outlined" sx={(theme) => ({ minWidth: 22, height: 20, color: theme.palette.secondary.main, borderColor: theme.palette.secondary.main, fontWeight: 700, fontSize: '0.7rem', borderRadius: 4 })} />;
      }
      return <Chip key={i} label={code} size="small" variant="outlined" sx={{ minWidth: 22, height: 20, color: '#757575', borderColor: '#bdbdbd', fontWeight: 700, fontSize: '0.7rem', borderRadius: 4 }} />;
    });
  };

  // Transform allSummaries into grouped table rows (grouped by itemName)
  const tableRows = useMemo(() => {
    const rows = [];
    const spanNames = spansToUse.map(s => s.scenario || 'unnamed');

    for (const [sectionName, items] of Object.entries(allSummaries)) {
      if (!Array.isArray(items)) continue;

      // New structure: items are grouped by ItemName, with Processes array
      for (const item of items) {
        const itemName = item.ItemName || '';
        if (!itemName) continue;

        // Extract item-level metadata (for complex sections)
        const itemMetadata = {};
        if (item.FileInfo) itemMetadata.FileInfo = item.FileInfo;
        if (item.CertInfo) itemMetadata.CertInfo = item.CertInfo;
        if (item.ProcInfo) {
          itemMetadata.ProcInfo = item.ProcInfo;
          // Extract Details for ProcessLifeCycles
          if (sectionName === 'ProcessLifeCycles' && item.ProcInfo.Details) {
            itemMetadata.Details = item.ProcInfo.Details;
          }
          // Store children for ProcessLifeCycles
          if (sectionName === 'ProcessLifeCycles' && item.ProcInfo.Children && Array.isArray(item.ProcInfo.Children)) {
            itemMetadata.Children = item.ProcInfo.Children;
          }
        }
        // Extract BitsInfo from first process for BitsFilesInfo
        if (sectionName === 'BitsFilesInfo' && item.Processes && Array.isArray(item.Processes) && item.Processes.length > 0) {
          const firstProc = item.Processes[0];
          if (firstProc && firstProc.BitsInfo && typeof firstProc.BitsInfo === 'object' && !Array.isArray(firstProc.BitsInfo)) {
            itemMetadata.BitsInfo = firstProc.BitsInfo;
          }
        }

        // Handle Processes array (new structure) - group into a single row with multiple process lines
        if (item.Processes && Array.isArray(item.Processes) && item.Processes.length > 0) {
          const processLines = [];
          
          for (const proc of item.Processes) {
            const processName = proc.ProcessName || 'unknown';
            const spanData = {};

            // Handle boolean sections (Spans)
            if (proc.Spans && typeof proc.Spans === 'object') {
              for (const spanName of spanNames) {
                if (proc.Spans && typeof proc.Spans === 'object') {
                  spanData[spanName] = proc.Spans[spanName] === true;
                } else {
                  spanData[spanName] = false;
                }
              }
              processLines.push({
                processName,
                spanData,
                dataType: 'boolean'
              });
            }
            // Handle lifecycle sections (LifeCycle)
            else if (proc.LifeCycle !== undefined) {
              for (const spanName of spanNames) {
                if (typeof proc.LifeCycle === 'string') {
                  // Single lifecycle string, apply to all spans
                  spanData[spanName] = proc.LifeCycle;
                } else if (proc.LifeCycle && typeof proc.LifeCycle === 'object' && !Array.isArray(proc.LifeCycle)) {
                  // Map of span names to lifecycle strings
                  // Only use the value if the key exists in the object - no fallback to 'all'
                  if (spanName in proc.LifeCycle) {
                    spanData[spanName] = proc.LifeCycle[spanName] || '';
                  } else {
                    spanData[spanName] = '';
                  }
                } else {
                  spanData[spanName] = '';
                }
              }
              processLines.push({
                processName,
                spanData,
                dataType: 'lifecycle'
              });
            }
          }
          
          // Create a single grouped row with multiple process lines
          if (processLines.length > 0) {
            rows.push({
              sectionName,
              itemName,
              processLines,
              metadata: Object.keys(itemMetadata).length > 0 ? itemMetadata : null,
              isGrouped: true
            });
          }
        }
        // Backward compatibility: Handle old structure if it exists
        else {
          // Handle ItemsAndLifeCycles (old structure)
          if (item.ItemsAndLifeCycles && typeof item.ItemsAndLifeCycles === 'object' && !Array.isArray(item.ItemsAndLifeCycles)) {
            for (const [oldItemName, lifeCycleMap] of Object.entries(item.ItemsAndLifeCycles)) {
              const spanData = {};
              for (const spanName of spanNames) {
                if (typeof lifeCycleMap === 'string') {
                  spanData[spanName] = lifeCycleMap;
                } else if (lifeCycleMap && typeof lifeCycleMap === 'object' && !Array.isArray(lifeCycleMap)) {
                  // Only use the value if the key exists in the object - no fallback to 'all'
                  if (spanName in lifeCycleMap) {
                    spanData[spanName] = lifeCycleMap[spanName] || '';
                  } else {
                    spanData[spanName] = '';
                  }
                } else {
                  spanData[spanName] = '';
                }
              }
              rows.push({
                sectionName,
                processName: item.ProcessName || 'unknown',
                itemName: oldItemName,
                spanData,
                dataType: 'lifecycle',
                metadata: null
              });
            }
          }
          // Handle ItemNames (old structure)
          else if (item.ItemNames && typeof item.ItemNames === 'object' && !Array.isArray(item.ItemNames)) {
            for (const [oldItemName, spansObj] of Object.entries(item.ItemNames)) {
              const spanData = {};
              for (const spanName of spanNames) {
                if (spansObj && typeof spansObj === 'object') {
                  spanData[spanName] = spansObj[spanName] === true;
                } else {
                  spanData[spanName] = false;
                }
              }
              rows.push({
                sectionName,
                processName: item.ProcessName || 'unknown',
                itemName: oldItemName,
                spanData,
                dataType: 'boolean',
                metadata: null
              });
            }
          }
          // Handle Items array (old structure)
          else if (item.Items && Array.isArray(item.Items)) {
            for (const itemEntry of item.Items) {
              const oldItemName = itemEntry.Thumbprint || itemEntry.FileName || itemEntry.UPid || itemEntry.ItemName || '';
              const spanData = {};
              
              if (itemEntry.LifeCycle) {
                if (typeof itemEntry.LifeCycle === 'string') {
                  for (const spanName of spanNames) {
                    spanData[spanName] = itemEntry.LifeCycle;
                  }
                } else if (itemEntry.LifeCycle && typeof itemEntry.LifeCycle === 'object') {
                  for (const spanName of spanNames) {
                    spanData[spanName] = itemEntry.LifeCycle[spanName] || '';
                  }
                }
              }

              const metadata = {};
              if (itemEntry.FileInfo) metadata.FileInfo = itemEntry.FileInfo;
              if (itemEntry.CertInfo) metadata.CertInfo = itemEntry.CertInfo;
              if (itemEntry.ProcInfo) metadata.ProcInfo = itemEntry.ProcInfo;
              
              if (sectionName === 'ProcessLifeCycles') {
                if (itemEntry.Details) {
                  metadata.Details = itemEntry.Details;
                } else if (itemEntry.ProcInfo?.Actions && Array.isArray(itemEntry.ProcInfo.Actions)) {
                  for (const action of itemEntry.ProcInfo.Actions) {
                    if (action.Action?.includes('ProcessStart') && action.Details) {
                      metadata.Details = action.Details;
                      break;
                    }
                  }
                }
              }
              
              if (sectionName === 'ProcessLifeCycles' && itemEntry.Children && Array.isArray(itemEntry.Children)) {
                const childrenData = [];
                for (const child of itemEntry.Children) {
                  let childName, childLifecycleMap, childDetails;
                  
                  if (typeof child === 'string') {
                    const match = child.match(/Child \[([^\]]+)\] LifeCycle \[([^\]]+)\](.*)/);
                    if (match) {
                      childName = match[1];
                      const childLifecycle = match[2];
                      childDetails = match[3] || '';
                      childLifecycleMap = {};
                      for (const spanName of spanNames) {
                        childLifecycleMap[spanName] = childLifecycle;
                      }
                    } else {
                      continue;
                    }
                  } else if (child && typeof child === 'object') {
                    childName = child.name;
                    childLifecycleMap = child.lifecycleMap || {};
                    childDetails = child.details || '';
                  } else {
                    continue;
                  }
                  
                  childrenData.push({
                    name: childName,
                    lifecycleMap: childLifecycleMap,
                    details: childDetails
                  });
                }
                if (childrenData.length > 0) {
                  metadata.Children = childrenData;
                }
              }
              
              for (const [key, value] of Object.entries(itemEntry)) {
                if (key !== 'LifeCycle' && key !== 'FileInfo' && key !== 'CertInfo' && key !== 'ProcInfo' && key !== 'Children' &&
                    value !== null && value !== undefined && value !== '') {
                  metadata[key] = value;
                }
              }

              rows.push({
                sectionName,
                processName: item.ProcessName || 'unknown',
                itemName: oldItemName,
                spanData,
                dataType: 'lifecycle',
                metadata: Object.keys(metadata).length > 0 ? metadata : null
              });
            }
          }
        }
      }
    }

    if (installerFileInfo) {
      // Name the installer row: FileName, else the PE original name (Windows), else the
      // leaf of the run's Executable from metadata.json (a .pkg/.dmg/.app carries no PE
      // fields and an autowatch run may leave FileName empty), else a literal.
      const leaf = (p) => (typeof p === 'string' ? p.replace(/\\/g, '/').split('/').filter(Boolean).pop() : '') || '';
      const fileName = installerFileInfo.FileName
        || installerFileInfo.PeOriginalFilename
        || leaf(installerFileInfo.InstallerPath)
        || leaf(metadata?.Executable)
        || 'Installer';
      const fileInfoMeta = Object.fromEntries(
        Object.entries(installerFileInfo)
          .filter(([key, value]) => key !== 'FileName' && value != null && value !== '')
      );

      rows.unshift({
        sectionName: 'InstallerInfo',
        itemName: fileName,
        processLines: [{
          processName: 'installer',
          spanData: Object.fromEntries(spanNames.map(s => [s, false])),
          dataType: 'boolean'
        }],
        metadata: { FileInfo: fileInfoMeta },
        isGrouped: true
      });
    }

    return rows;
  }, [allSummaries, spansToUse, installerFileInfo, metadata]);

  // Filter tableRows based on search query
  const filteredTableRows = useMemo(() => {
    if (!searchQuery.trim()) {
      return tableRows;
    }

    const query = searchQuery.toLowerCase();
    
    return tableRows.filter(row => {
      // Search in sectionName
      if (row.sectionName?.toLowerCase().includes(query)) return true;
      
      // Search in itemName
      if (row.itemName?.toLowerCase().includes(query)) return true;
      
      // Handle grouped rows
      if (row.isGrouped && row.processLines) {
        // Search in process names within grouped rows
        for (const processLine of row.processLines) {
          if (processLine.processName?.toLowerCase().includes(query)) return true;
          
          // Search in lifecycle strings for grouped rows
          if (processLine.spanData) {
            for (const lifecycleValue of Object.values(processLine.spanData)) {
              if (String(lifecycleValue || '').toLowerCase().includes(query)) return true;
            }
          }
        }
      } else {
        // Search in processName (for non-grouped rows)
        if (row.processName?.toLowerCase().includes(query)) return true;
        
        // Search in lifecycle strings (for non-grouped rows)
        if (row.spanData) {
          for (const lifecycleValue of Object.values(row.spanData)) {
            if (String(lifecycleValue || '').toLowerCase().includes(query)) return true;
          }
        }
      }
      
      // Search in metadata.Details
      if (row.metadata?.Details?.toLowerCase().includes(query)) return true;
      
      // Search in FileInfo properties
      if (row.metadata?.FileInfo) {
        const fileInfoStr = JSON.stringify(row.metadata.FileInfo).toLowerCase();
        if (fileInfoStr.includes(query)) return true;
      }
      
      // Search in CertInfo properties
      if (row.metadata?.CertInfo) {
        const certInfoStr = JSON.stringify(row.metadata.CertInfo).toLowerCase();
        if (certInfoStr.includes(query)) return true;
      }
      
      // Search in ProcInfo properties
      if (row.metadata?.ProcInfo) {
        const procInfoStr = JSON.stringify(row.metadata.ProcInfo).toLowerCase();
        if (procInfoStr.includes(query)) return true;
      }
      
      // Search in children names
      if (row.metadata?.Children && Array.isArray(row.metadata.Children)) {
        for (const child of row.metadata.Children) {
          if (child.name?.toLowerCase().includes(query)) return true;
          if (child.details?.toLowerCase().includes(query)) return true;
        }
      }
      
      return false;
    });
  }, [tableRows, searchQuery]);

  const scopedCount = useMemo(() => {
    if (!Array.isArray(allRows)) return 0;
    if (startMs === null && endMs === null) return allRows.length;
    const s = typeof startMs === 'number' ? startMs : -Infinity;
    const e = typeof endMs === 'number' ? endMs : Infinity;
    return allRows.filter(ev => typeof ev.VideoOffset === 'number' && ev.VideoOffset >= s && ev.VideoOffset <= e).length;
  }, [allRows, startMs, endMs]);

  const formatTime = (ms) => {
    // ms is a video offset in milliseconds — format as M:SS.mmm
    if (typeof ms !== 'number' || isNaN(ms)) return '-';
    const sign = ms < 0 ? '-' : '';
    const abs = Math.abs(ms);
    const minutes = Math.floor(abs / 60000);
    const seconds = Math.floor((abs % 60000) / 1000);
    const msPart = Math.floor(abs % 1000).toString().padStart(3, '0');
    return `${sign}${minutes}:${seconds.toString().padStart(2, '0')}.${msPart}`;
  };

  return (
    <Box sx={{ p: 1, flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Dialog open={recomputeConfirmOpen} onClose={() => setRecomputeConfirmOpen(false)}>
        <DialogTitle>Recompute event summaries?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This recomputes the event summaries for this run and refreshes the cached copy.
            It can take a while for large logs. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecomputeConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => { setRecomputeConfirmOpen(false); recompute(); }}
            variant="contained"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
          >
            Recompute
          </Button>
        </DialogActions>
      </Dialog>
      {/* Sticky controls: scenario info, search, expand/collapse */}
      <Box sx={{ position: 'sticky', top: -8, zIndex: 10, backgroundColor: 'background.paper', pt: 1.25, pb: 0.75, mb: 1, mx: -1, px: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <TextField
            size="small"
            placeholder="Search all fields..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              maxWidth: 400,
              '& .MuiInputBase-root': { height: 32 },
              '& .MuiInputBase-input': { py: 0, fontSize: 13 }
            }}
          />
          {(() => {
            const allSectionNames = new Set();
            if (installerFileInfo) allSectionNames.add('InstallerInfo');
            for (const [sectionName] of Object.entries(allSummaries || {})) {
              allSectionNames.add(sectionName);
            }
            const allExpanded = allSectionNames.size > 0 && Array.from(allSectionNames).every(name => expandedSections.has(name));
            return (
              <Button
                size="small"
                startIcon={allExpanded ? <UnfoldLessIcon sx={{ fontSize: 16 }} /> : <UnfoldMoreIcon sx={{ fontSize: 16 }} />}
                onClick={toggleAllSections}
                variant="outlined"
                sx={{ height: 32, minHeight: 32, py: 0 }}
              >
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </Button>
            );
          })()}
          {recompute && (
            <Button
              size="small"
              onClick={() => setRecomputeConfirmOpen(true)}
              variant="outlined"
              disabled={summariesLoading}
              title="Recompute event summaries"
              sx={{ height: 32, minHeight: 32, minWidth: 32, px: 0.5, py: 0, flexShrink: 0 }}
            >
              <RefreshIcon sx={{ fontSize: 18 }} />
            </Button>
          )}
          <Button
            size="small"
            onClick={handleDownloadSummaries}
            variant="outlined"
            disabled={summariesLoading || !allSummaries || Object.keys(allSummaries || {}).length === 0}
            title="Download event_summaries.json"
            sx={{ height: 32, minHeight: 32, minWidth: 32, px: 0.5, py: 0, flexShrink: 0 }}
          >
            <DownloadIcon sx={{ fontSize: 18 }} />
          </Button>
          <Typography variant="caption" sx={{ marginLeft: 'auto', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {selectedScenario && (
              <Typography component="span" variant="caption" sx={{ fontSize: 12, mr: 0.75 }} color="text.secondary">
                {selectedScenario.name || 'unnamed'} [{formatTime(selectedScenario.startMs)} – {selectedScenario.endMs ? formatTime(selectedScenario.endMs) : 'ongoing'}]
              </Typography>
            )}
            <Typography component="span" variant="caption" sx={{ fontWeight: 600, fontSize: 12, color: 'primary.main' }}>
              {scopedCount.toLocaleString()} events
            </Typography>
          </Typography>
        </Box>
      </Box>

      {/* Loading indicator for summaries computation */}
      {summariesLoading && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          py: 4,
          gap: 2 
        }}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Computing event summaries...
          </Typography>
          {summariesProgress && summariesProgress.total > 0 && (
            <Box sx={{ width: '100%', maxWidth: 350 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: summariesProgress.isSlow ? 'warning.main' : 'text.secondary',
                    fontWeight: summariesProgress.isSlow ? 600 : 400
                  }}
                >
                  {summariesProgress.section}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summariesProgress.current}/{summariesProgress.total}
                </Typography>
              </Box>
              <Box sx={{ 
                width: '100%', 
                height: 6, 
                bgcolor: 'action.hover', 
                borderRadius: 3,
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  width: `${(summariesProgress.current / summariesProgress.total) * 100}%`,
                  height: '100%',
                  bgcolor: summariesProgress.isSlow ? 'warning.main' : 'primary.main',
                  transition: 'width 0.3s ease'
                }} />
              </Box>
              {summariesProgress.isSlow && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary', 
                    display: 'block', 
                    mt: 1, 
                    textAlign: 'center',
                    fontStyle: 'italic'
                  }}
                >
                  Processing large dataset - please wait...
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Table area */}
      {!summariesLoading && (
      <Box sx={{ overflow: 'auto', position: 'relative' }}>
        {filteredTableRows.length > 0 ? (
          <TableContainer 
            component={Paper} 
                                        sx={{
              maxHeight: '100%',
              position: 'relative',
              overflow: 'auto',
              width: '100%'
            }}
          >
            <Table 
              stickyHeader 
              size="small" 
              sx={{ 
                tableLayout: 'fixed',
                width: '100%',
                '& .MuiTableCell-root': { fontSize: '0.75rem', padding: '4px 8px' },
                '& .MuiTableHead-root': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: 'background.paper'
                }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: '30%', maxWidth: 300, backgroundColor: 'background.paper', whiteSpace: 'normal', wordBreak: 'break-word' }}>Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '20%', maxWidth: 200, backgroundColor: 'background.paper' }}>Process Name</TableCell>
                  {spansToUse.map((span) => {
                    const spanName = span.scenario || 'unnamed';
                    const isSelected = spanName === selectedSpanName;
                    // Remaining space (50%) divided equally among spans, but capped at 100px
                    const spanColumnWidth = spansToUse.length > 0 ? `${50 / spansToUse.length}%` : '100px';
                    return (
                      <TableCell
                        key={spanName}
                        sx={{
                          fontWeight: 700,
                          width: spanColumnWidth,
                          maxWidth: 100,
                          bgcolor: isSelected ? 'action.selected' : 'background.paper',
                          borderLeft: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                          borderRight: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          textAlign: 'center',
                        }}
                      >
                        {spanName}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // Group rows by section
                  const groupedRows = {};
                  for (const row of filteredTableRows) {
                    if (!groupedRows[row.sectionName]) {
                      groupedRows[row.sectionName] = [];
                    }
                    groupedRows[row.sectionName].push(row);
                  }

                  const result = [];
                  const sectionOrder = Object.keys(groupedRows);
                  if (sectionOrder.includes('InstallerInfo')) {
                    sectionOrder.splice(sectionOrder.indexOf('InstallerInfo'), 1);
                    sectionOrder.unshift('InstallerInfo');
                  }
                  for (const sectionName of sectionOrder) {
                    const rows = groupedRows[sectionName];
                    const isExpanded = expandedSections.has(sectionName);
                    const isLoading = loadingSections.has(sectionName);
                    
                    // Sort rows by itemName case-insensitively within each section
                    const sortedRows = [...rows].sort((a, b) => {
                      const aName = (a.itemName || '').toLowerCase();
                      const bName = (b.itemName || '').toLowerCase();
                      return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
                    });
                    
                    // Update row count to reflect grouped rows correctly
                    const uniqueItemCount = new Set(sortedRows.map(r => r.itemName)).size;
                    
                    // Section header row
                    result.push(
                      <TableRow
                        key={`section-header-${sectionName}`}
                        sx={{
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'action.hover' },
                          cursor: 'pointer',
                        }}
                        onClick={() => toggleSection(sectionName)}
                      >
                        <TableCell colSpan={3 + spansToUse.length} sx={{ fontWeight: 700, py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isLoading ? (
                              <CircularProgress size={16} sx={{ ml: 0.5 }} />
                            ) : (
                              <IconButton size="small" sx={{ padding: '2px' }}>
                                {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                              </IconButton>
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 700 }} title={sectionName}>
                              {getBucketLabel(sectionName, platform)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                              ({uniqueItemCount} {uniqueItemCount === 1 ? 'item' : 'items'})
                            </Typography>
                            {isLoading && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                Loading...
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );

                    // Section rows (collapsible) - conditionally render based on expanded state
                    if (isExpanded) {
                      // Apply section limit for progressive loading
                      const limit = getSectionLimit(sectionName);
                      const displayRows = sortedRows.slice(0, limit);
                      const hasMore = sortedRows.length > limit;
                      const showingAll = limit >= sortedRows.length;
                      
                      displayRows.forEach((row, idx) => {
                        // Use GroupedTableRow for grouped rows, ExpandableTableRow for non-grouped
                        if (row.isGrouped && row.processLines) {
                          result.push(
                            <GroupedTableRow
                              key={`${row.sectionName}-${row.itemName}-${idx}`}
                              row={row}
                              spansToUse={spansToUse}
                              selectedSpanName={selectedSpanName}
                              theme={theme}
                              renderLifecycleChips={renderLifecycleChips}
                              expandedEventsRows={expandedEventsRows}
                              toggleEventsRow={toggleEventsRow}
                              allRows={allRows}
                              setSeekTo={setSeekTo}
                              formatTime={formatTime}
                              cachedFilterPredicate={cachedFilterPredicate}
                              platform={platform}
                            />
                          );
                        } else {
                          result.push(
                            <ExpandableTableRow
                              key={`${row.sectionName}-${row.processName}-${row.itemName}-${idx}`}
                              row={row}
                              spansToUse={spansToUse}
                              selectedSpanName={selectedSpanName}
                              theme={theme}
                              renderLifecycleChips={renderLifecycleChips}
                              expandedEventsRows={expandedEventsRows}
                              toggleEventsRow={toggleEventsRow}
                              allRows={allRows}
                              setSeekTo={setSeekTo}
                              formatTime={formatTime}
                              cachedFilterPredicate={cachedFilterPredicate}
                              platform={platform}
                            />
                          );
                        }
                      });
                      
                      // Add "Show more" / "Show less" row if there are many items
                      if (sortedRows.length > DEFAULT_SECTION_LIMIT) {
                        result.push(
                          <TableRow key={`${sectionName}-pagination`}>
                            <TableCell 
                              colSpan={3 + spansToUse.length} 
                              sx={{ 
                                py: 1, 
                                textAlign: 'center',
                                bgcolor: 'action.hover'
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Showing {Math.min(limit, sortedRows.length)} of {sortedRows.length} items
                                </Typography>
                                {hasMore && (
                                  <>
                                    <Button 
                                      size="small" 
                                      variant="text"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        showMoreInSection(sectionName, sortedRows.length);
                                      }}
                                    >
                                      Show more
                                    </Button>
                                    <Button 
                                      size="small" 
                                      variant="text"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        showAllInSection(sectionName, sortedRows.length);
                                      }}
                                    >
                                      Show all
                                    </Button>
                                  </>
                                )}
                                {showingAll && limit > DEFAULT_SECTION_LIMIT && (
                                  <Button 
                                    size="small" 
                                    variant="text"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      showLessInSection(sectionName);
                                    }}
                                  >
                                    Show less
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    }
                  }
                  return result;
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>No data available</Typography>
        )}
      </Box>
      )}
    </Box>
  );
});

export default EventSummaries;
